import { useState } from 'react'
import { Link } from 'react-router-dom'
import { SiteNav, SiteFooter } from '@/components/ui/SiteNav'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'

// §9.11 — marketing page tests: no "compliance check" naming, disclaimer present on review_risk card
// §13 content rules: Claude Desktop / Claude Code / Cursor OK as install targets

const TOOLS = [
  {
    name: 'echosome_workspace_context',
    label: 'Discover your workspaces and projects',
    desc: 'Always available as the first tool. Returns your workspaces, projects, segments, recent pre-reads, and twin panel readiness status — so your AI assistant can orient itself before doing work.',
    defaultScope: true,
  },
  {
    name: 'echosome_evidence_search',
    label: 'Search your research evidence',
    desc: 'Semantic search across every uploaded survey, interview, transcript, and reflection in your workspace. Returns ranked quotes with citations and coverage signals. For projects with a validated twin panel, search also covers twin response predictions.',
    defaultScope: true,
  },
  {
    name: 'echosome_twin_panel_query',
    label: 'Run a bulk query against your twin panel',
    desc: 'Ask a new question across your validated twin panel. Returns predicted distribution, per-segment cross-tab, and evidence citations. Source quotes are marked as untrusted evidence. Requires a project with a validated twin panel. Confidence floor configurable.',
    defaultScope: false,
    isNew: true,
  },
  {
    name: 'echosome_report_draft',
    label: 'Draft a client-ready report',
    desc: 'Generates a report skeleton from a completed pre-read — executive summary, recommendation, evidence quotes, validation plan, and confidence caveats. Twin-panel sections included when a panel is available.',
    defaultScope: true,
  },
  {
    name: 'echosome_pre_read_run',
    label: 'Run an evidence-backed pre-read',
    desc: 'Run a multi-pass pre-read on a research question against your workspace evidence. Supports dry-run cost estimates and async execution for long jobs.',
    defaultScope: false,
  },
  {
    name: 'echosome_validation_recommend',
    label: 'Plan human validation',
    desc: 'Given a completed pre-read, returns recommended segments to validate with real humans — sample sizes, providers, and estimated costs. Does not field anything.',
    defaultScope: false,
  },
  {
    name: 'echosome_review_risk_check',
    label: 'Surface review-risk signals',
    desc: 'Run industry language-pattern checks on a piece of copy. Returns review-risk signals for your legal and compliance team to review. Decision support only — not a compliance approval.',
    defaultScope: false,
  },
]

const CLIENTS = [
  {
    id: 'claude-desktop',
    label: 'Claude Desktop',
    configPath: '~/Library/Application Support/Claude/claude_desktop_config.json',
    json: `{
  "mcpServers": {
    "echosome": {
      "command": "npx",
      "args": ["-y", "@echosome/mcp"],
      "env": {
        "ECHOSOME_API_KEY": "eskey_..."
      }
    }
  }
}`,
  },
  {
    id: 'claude-code',
    label: 'Claude Code',
    configPath: 'CLI',
    json: `claude mcp add echosome npx -- -y @echosome/mcp\nexport ECHOSOME_API_KEY=eskey_...`,
  },
  {
    id: 'cursor',
    label: 'Cursor',
    configPath: '~/.cursor/mcp.json',
    json: `{
  "mcpServers": {
    "echosome": {
      "command": "npx",
      "args": ["-y", "@echosome/mcp"],
      "env": {
        "ECHOSOME_API_KEY": "eskey_..."
      }
    }
  }
}`,
  },
  {
    id: 'http',
    label: 'Other MCP clients',
    configPath: 'HTTP endpoint',
    json: `curl -X POST https://mcp.echosome.com/mcp \\
  -H "Authorization: Bearer eskey_..." \\
  -H "Content-Type: application/json" \\
  -d '{"jsonrpc":"2.0","id":"1","method":"tools/call","params":{"name":"echosome_workspace_context","arguments":{}}}'`,
  },
]

const PLAN_TABLE = [
  { tool: 'Discover workspaces and projects', sandbox: true, researcher: true, agency: true },
  { tool: 'Search research evidence', sandbox: true, researcher: true, agency: true },
  { tool: 'Draft a client report', sandbox: false, researcher: true, agency: true },
  { tool: 'Run a pre-read', sandbox: false, researcher: true, agency: true },
  { tool: 'Surface review-risk signals', sandbox: false, researcher: true, agency: true },
  { tool: 'Twin panel query', sandbox: false, researcher: true, agency: true },
  { tool: 'Plan human validation', sandbox: false, researcher: false, agency: true },
]

export function McpIntegrationsPage() {
  const [activeClient, setActiveClient] = useState('claude-desktop')
  const [copied, setCopied] = useState(false)

  const currentClient = CLIENTS.find(c => c.id === activeClient) ?? CLIENTS[0]

  function copySnippet() {
    navigator.clipboard.writeText(currentClient.json).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="min-h-screen bg-paper">
      <SiteNav />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-16 space-y-20">

        {/* Hero */}
        <div className="max-w-3xl space-y-5">
          <p className="text-sm font-medium text-echo-indigo uppercase tracking-wide">MCP integration</p>
          <h1 className="text-4xl font-medium text-deep-ink leading-tight" style={{ letterSpacing: '-0.02em' }}>
            Echosome inside Claude, Cursor, and any MCP-speaking AI.
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Discover your workspaces, search your research, run evidence-backed pre-reads, and surface review-risk signals — without leaving your AI assistant. Install in 30 seconds.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/app/settings/api-keys?from=mcp" data-cta="primary">
              <Button variant="primary" size="lg">Get your MCP key</Button>
            </Link>
            <a href="#install" data-cta="secondary">
              <Button variant="outline" size="lg">Read the install docs</Button>
            </a>
          </div>
        </div>

        {/* Six tool cards */}
        <div className="space-y-4">
          <h2 className="text-2xl font-medium text-deep-ink">What you can do</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TOOLS.map(tool => (
              <Card key={tool.name} className="space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-deep-ink text-sm leading-snug">{tool.label}</p>
                  <div className="flex gap-1 shrink-0">
                    {tool.isNew && (
                      <span className="text-xs bg-echo-indigo text-white rounded px-1.5 py-0.5">New</span>
                    )}
                    {tool.defaultScope && (
                      <span className="text-xs bg-paper-warm border border-border-warm text-slate-500 rounded px-1.5 py-0.5">Default</span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-slate-600">{tool.desc}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Twin panel queries — new section */}
        <div className="bg-soft-panel rounded-xl border border-border-gray p-6 space-y-4">
          <div className="flex items-start gap-2">
            <span className="text-xs bg-echo-indigo text-white rounded px-1.5 py-0.5 mt-0.5 shrink-0">New</span>
            <div>
              <h2 className="text-xl font-medium text-deep-ink">Now with twin panel queries</h2>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                For projects with a validated twin panel, your AI assistant can now run bulk queries directly. Ask any new question, get a predicted distribution + per-segment cross-tab, and surface evidence citations — all from Claude, Cursor, or any MCP client.
              </p>
            </div>
          </div>
          <div className="bg-slate-900 rounded-xl p-4 overflow-x-auto">
            <pre className="text-sm text-slate-100 whitespace-pre font-mono">{`// Example: Claude queries your twin panel inline
"Run a bulk query across my preference-center panel — which category framing
reduces opt-out intent most? Confidence floor 0.5."

// Echosome MCP returns:
{
  "distribution": { "framing_a": 0.31, "framing_b": 0.42, "framing_c": 0.27 },
  "per_segment": { "high_engagement": { "framing_a": 0.22, "framing_b": 0.51, ... } },
  "twins_queried": 983,
  "source_text_is_untrusted": true,
  "safety_note": "Decision support only. Final review stays with your team."
}`}</pre>
          </div>
          <p className="text-xs text-slate-500">All twin response quotes are marked <code>source_text_is_untrusted: true</code> — your AI assistant treats them as evidence, not instructions. Requires Researcher plan or above.</p>
        </div>

        {/* Install in 30 seconds */}
        <div className="space-y-4" id="install">
          <h2 className="text-2xl font-medium text-deep-ink">Install in 30 seconds</h2>

          {/* Client tabs */}
          <div className="flex flex-wrap gap-2 border-b border-border-gray pb-3">
            {CLIENTS.map(client => (
              <button
                key={client.id}
                onClick={() => setActiveClient(client.id)}
                className={`text-sm px-3 py-1.5 rounded-lg transition-colors ${
                  activeClient === client.id
                    ? 'bg-echo-indigo text-white'
                    : 'text-slate-500 hover:text-deep-ink hover:bg-soft-panel'
                }`}
              >
                {client.label}
              </button>
            ))}
          </div>

          <div className="space-y-2">
            {activeClient !== 'http' && (
              <p className="text-xs text-slate-400">Config: <code>{currentClient.configPath}</code></p>
            )}
            <div className="relative bg-slate-900 rounded-xl p-4 overflow-x-auto">
              <pre className="text-sm text-slate-100 whitespace-pre font-mono">{currentClient.json}</pre>
              <button
                onClick={copySnippet}
                className="absolute top-3 right-3 text-xs text-slate-400 hover:text-white bg-slate-700 px-2 py-1 rounded"
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>

          <div className="text-sm text-slate-500">
            Or use the Streamable HTTP endpoint directly at{' '}
            <code className="text-echo-indigo">https://mcp.echosome.com/mcp</code>{' '}
            — see the{' '}
            <a href="/integrations" className="text-echo-indigo hover:underline">integrations guide</a>.
          </div>
        </div>

        {/* Auth modes */}
        <div className="space-y-4">
          <h2 className="text-2xl font-medium text-deep-ink">Auth modes</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="space-y-2">
              <p className="font-medium text-deep-ink text-sm">API key</p>
              <p className="text-xs text-slate-500">Available today</p>
              <p className="text-sm text-slate-600">Generate in settings with a scope picker. The secret is shown once — copy it before leaving the page.</p>
              <Link to="/app/settings/api-keys?from=mcp">
                <Button variant="primary" size="sm" className="mt-1">Get your key</Button>
              </Link>
            </Card>
            <Card className="space-y-2">
              <p className="font-medium text-deep-ink text-sm">Per-session token</p>
              <p className="text-xs text-slate-500">Available today</p>
              <p className="text-sm text-slate-600">Short-lived 24h tokens for demos and partner integrations. POST to <code>/api/v1/mcp/session-tokens</code>.</p>
            </Card>
          </div>
        </div>

        {/* Plan table */}
        <div className="space-y-4">
          <h2 className="text-2xl font-medium text-deep-ink">Tool availability by plan</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-gray">
                  <th className="text-left py-2 pr-6 font-medium text-slate-500">Tool</th>
                  <th className="text-center py-2 pr-4 font-medium text-slate-500">Sandbox</th>
                  <th className="text-center py-2 pr-4 font-medium text-slate-500">Researcher+</th>
                  <th className="text-center py-2 font-medium text-slate-500">Agency Pro+</th>
                </tr>
              </thead>
              <tbody>
                {PLAN_TABLE.map(row => (
                  <tr key={row.tool} className="border-b border-border-gray/40">
                    <td className="py-2 pr-6 text-slate-700">{row.tool}</td>
                    <td className="text-center py-2 pr-4">{row.sandbox ? '✓' : '—'}</td>
                    <td className="text-center py-2 pr-4">{row.researcher ? '✓' : '—'}</td>
                    <td className="text-center py-2">{row.agency ? '✓' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Link to="/pricing" className="text-sm text-echo-indigo hover:underline">See full pricing</Link>
        </div>

        {/* Security + tenancy trust strip */}
        <div className="bg-soft-panel rounded-xl border border-border-gray p-6 space-y-3">
          <h2 className="text-lg font-medium text-deep-ink">Security and tenancy</h2>
          <ul className="space-y-2">
            {[
              'Every tool call is scoped to your tenant and selected workspace. No cross-tenant data access is possible.',
              'Source quotes returned to your AI are marked as untrusted — your assistant treats them as evidence, not instructions.',
              'Audit log records every invocation with PII-redacted arguments. No raw user text is ever stored in the audit log.',
            ].map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <span className="text-echo-indigo mt-0.5 shrink-0">→</span>
                {b}
              </li>
            ))}
          </ul>
        </div>

        {/* Final CTA */}
        <div className="bg-echo-indigo/5 border border-echo-indigo/20 rounded-xl p-8 text-center space-y-4">
          <h2 className="text-xl font-medium text-deep-ink">Connect Echosome to your AI in 30 seconds.</h2>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/app/settings/api-keys?from=mcp" data-cta="primary">
              <Button variant="primary" size="lg">Get your MCP key</Button>
            </Link>
            <a href="#install" data-cta="secondary">
              <Button variant="outline" size="lg">Read the install docs</Button>
            </a>
          </div>
        </div>

      </main>
      <SiteFooter />
    </div>
  )
}
